import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faCode, faQuestionCircle, faLightbulb, faStar } from '@fortawesome/free-solid-svg-icons';

const MarkdownCalculator = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [solution, setSolution] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [{
      label: 'Calculation Result',
      data: [],
      borderColor: 'rgb(236, 72, 153)',
      tension: 0.1,
    }],
  });

  const calculate = () => {
    try {
      // Validate input
      if (!input.trim()) {
        throw new Error('Please enter a calculation.');
      }

      // Parse the input expression
      const expression = input.replace(/[^0-9+\-*/().\s]/g, '');
      
      // Check if the expression contains at least one operator
      if (!/[+\-*/]/.test(expression)) {
        throw new Error('Please include at least one operator (+, -, *, /) in your calculation.');
      }

      // Check if the expression has balanced parentheses
      let parenthesesCount = 0;
      for (const char of expression) {
        if (char === '(') parenthesesCount++;
        if (char === ')') parenthesesCount--;
        if (parenthesesCount < 0) throw new Error('Unbalanced parentheses in the calculation.');
      }
      if (parenthesesCount !== 0) throw new Error('Unbalanced parentheses in the calculation.');

      // Evaluate the expression
      const result = eval(expression);
      
      // Generate solution steps
      const steps = expression.split(/([+\-*/])/).filter(Boolean);
      if (steps.length < 3) {
        throw new Error('Please enter a valid mathematical expression with numbers and operators.');
      }

      const solutionSteps = steps.map((step, index) => {
        if (index % 2 === 0) {
          return `Number ${parseInt(step)}`;
        }
        return `Operation ${step}`;
      });

      // Initialize chart values
      const chartValues = [parseInt(steps[0])];
      
      // Calculate intermediate values for chart
      for (let i = 1; i < steps.length; i += 2) {
        const operation = steps[i];
        const currentNumber = parseInt(steps[i + 1]);
        const prevValue = chartValues[chartValues.length - 1];
        
        switch (operation) {
          case '+': chartValues.push(prevValue + currentNumber); break;
          case '-': chartValues.push(prevValue - currentNumber); break;
          case '*': chartValues.push(prevValue * currentNumber); break;
          case '/': chartValues.push(prevValue / currentNumber); break;
        }
      }

      // Update chart data
      const chartLabels = Array(steps.length).fill('');
      
      setChartData({
        labels: chartLabels,
        datasets: [{
          label: 'Calculation Progress',
          data: chartValues,
          borderColor: 'rgb(236, 72, 153)',
          tension: 0.1,
        }],
      });
      
      setResult(result);
      setSolution(solutionSteps.join(' → '));
      setError('');
    } catch (error) {
      setError(error.message || 'Invalid input. Please check your calculation.');
      setSolution('');
      setResult('');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Advanced Markdown Calculator | Math Tool with Graphs</title>
          <meta name="description" content="An advanced calculator that supports mathematical expressions, Markdown formatting, and visual graphs." />
          <meta name="keywords" content="markdown calculator, math tool, graph calculator, advanced calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Advanced Markdown Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Enter your calculations and see the results with visual graphs and step-by-step solutions.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculation:</label>
              <textarea
                rows="4"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter your calculation (e.g., 2 + 3 * 4)"
              />
            </div>
          </div>

          <button
            onClick={calculate}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-2xl font-semibold text-pink-600">{result}</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Solution Steps</h2>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-lg text-gray-700">{solution}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Calculation Progress</h2>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Line data={chartData} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-12">
          <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faQuestionCircle} className="mr-2 text-pink-600" />
            How to Use
          </h2>
          <div className="text-gray-600 bg-white rounded-lg shadow-md p-6">
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Calculation:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Use numbers and basic operators (+, -, *, /)</li>
                  <li>Use parentheses for order of operations</li>
                  <li>Spaces between numbers and operators are optional</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>View the final result</li>
                  <li>See step-by-step solution</li>
                  <li>Visualize calculation progress with graph</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faQuestionCircle} className="mr-2 text-pink-600" />
            FAQ
          </h2>
          <div className="text-gray-600 bg-white rounded-lg shadow-md p-6">
            <details className="mb-4">
              <summary className="font-semibold text-pink-600 cursor-pointer">What calculations are supported?</summary>
              <p className="mt-2">The calculator supports basic arithmetic operations including addition, subtraction, multiplication, and division.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-pink-600 cursor-pointer">How does the graph work?</summary>
              <p className="mt-2">The graph shows the calculation progress, with each point representing a step in the calculation.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-pink-600 cursor-pointer">Can I use parentheses?</summary>
              <p className="mt-2">Yes! Use parentheses to control the order of operations in your calculations.</p>
            </details>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faStar} className="mr-2 text-yellow-500" />
            Features & Benefits
          </h2>
          <div className="text-gray-600 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Features</h3>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Visual graph representation
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Step-by-step solution display
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Error handling and validation
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Responsive design
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-700 mt-8 mb-4">Benefits</h3>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Better understanding of calculations
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Visual learning aid
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Quick verification of results
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Free and easy to use
              </li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MarkdownCalculator;